import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const WhyAppsAreDangerous: React.FC = () => {
  useSEO(
    'Why Instagram Third-Party Apps Are Dangerous — Purgr',
    'Many Instagram apps that promise follower insights require your login credentials or use automation, putting your account at real risk. Here\'s what you need to know.'
  );

  return (
    <PageLayout title="Why Instagram Third-Party Apps Are Dangerous">
      <div className="space-y-8 text-white/70 text-sm leading-relaxed">

        <p className="text-white/80 text-base">
          Thousands of apps promise to show you who unfollowed you, boost your engagement, or grow
          your Instagram following. Many of them work — but at a cost most users don't understand until
          it's too late. Here's exactly what's happening behind the scenes, and how to stay safe.
        </p>

        {/* How most apps work */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">How most Instagram tracker apps actually work</p>
          <p className="text-white/60 mb-4">
            There are a few common ways these apps access your Instagram data, and none of them are risk-free:
          </p>
          <div className="space-y-3">
            {[
              {
                method: 'Direct login (username + password)',
                risk: 'High',
                color: 'red',
                body: 'The app logs into Instagram as you. Your credentials are stored on their servers. Instagram detects the login as a bot or unusual access, which can trigger a ban. If the app is breached, attackers have your password.',
              },
              {
                method: 'Instagram OAuth ("Login with Instagram")',
                risk: 'Medium–High',
                color: 'orange',
                body: 'The app requests permission to access your account data via Instagram\'s API. While this avoids sharing your password, it grants the app access to your follower list, posts, and profile data. Many of these apps violate Instagram\'s API terms, making your account vulnerable to suspension.',
              },
              {
                method: 'Browser extension scraping',
                risk: 'High',
                color: 'red',
                body: 'Some tools work as browser extensions that scrape Instagram while you\'re logged in. These tools automate actions (follows, unfollows, scrolling) in ways Instagram explicitly prohibits. Extensions also have access to everything you do in your browser.',
              },
            ].map(({ method, risk, color, body }) => (
              <div key={method} className="glass-card p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white/80 font-semibold text-sm">{method}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${color === 'red' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}`}>
                    Risk: {risk}
                  </span>
                </div>
                <p className="text-white/50 text-xs leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Instagram's terms */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-3 text-base">What Instagram's Terms of Service say</p>
          <p className="text-white/55 mb-3">
            Instagram's Terms of Use explicitly prohibit:
          </p>
          <ul className="space-y-2 text-white/50 text-xs">
            <li>• Creating accounts or collecting information in automated ways without permission</li>
            <li>• Selling, licensing, or purchasing account credentials</li>
            <li>• Using the service in a manner inconsistent with its intended purpose</li>
            <li>• Using third-party applications that interact with Instagram without authorization</li>
          </ul>
          <p className="text-white/50 mt-3 text-xs">
            Violations can result in temporary restrictions, permanent bans, or legal action — even if
            you didn't realize your app was violating the terms.
          </p>
        </div>

        {/* What can happen */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">What can actually happen to your account</p>
          <div className="space-y-3">
            {[
              { icon: '🚫', title: 'Temporary action blocks', body: 'Instagram limits your ability to follow, unfollow, like, or comment. These blocks can last hours to weeks.' },
              { icon: '⛔', title: 'Account suspension', body: 'Your account is temporarily or permanently disabled. You may lose years of content and followers with no appeal.' },
              { icon: '🔐', title: 'Account compromise', body: 'Apps that store your password insecurely can expose your credentials. Attackers can lock you out of your own account.' },
              { icon: '📊', title: 'Data misuse', body: 'Your follower list, messaging patterns, and behavior data can be sold to advertisers or data brokers without your knowledge.' },
            ].map(({ icon, title, body }) => (
              <div key={title} className="glass-card p-4 flex gap-4">
                <span className="text-xl flex-shrink-0">{icon}</span>
                <div>
                  <p className="text-white/80 font-semibold text-sm mb-1">{title}</p>
                  <p className="text-white/50 text-xs leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* The safe alternative */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">The safe alternative: use Instagram's own data export</p>
          <p className="text-white/60 mb-3">
            Instagram has a built-in feature that lets you download your own data — including your complete
            followers and following lists. This is the official, sanctioned way to access your data, and it
            doesn't involve any third-party apps logging into your account.
          </p>
          <p className="text-white/60 mb-3">
            Purgr uses exactly this approach. You download two files from Instagram's settings, upload them
            to Purgr, and get your results instantly — entirely in your browser. Purgr never sees your files,
            never touches your account, and requires no login of any kind.
          </p>
          <div className="glass-card p-4 border-green-500/20 bg-green-500/5">
            <p className="text-green-400 font-semibold text-sm mb-2">Zero risk to your account because:</p>
            <ul className="space-y-1 text-white/55 text-xs">
              <li>✓ No Instagram login or API access</li>
              <li>✓ No automation or bot-like behavior</li>
              <li>✓ Files processed locally in your browser</li>
              <li>✓ Instagram never knows you used Purgr</li>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-xl p-px ig-gradient-bg">
          <div className="rounded-xl bg-[#0D0D0D] px-6 py-6 text-center">
            <p className="text-white font-bold text-base mb-1">Check your followers safely — no login needed</p>
            <p className="text-white/50 text-sm mb-4">Use Instagram's own data export. Purgr never sees your account.</p>
            <Link
              to="/"
              className="btn-gradient inline-block text-white font-bold px-8 py-3 rounded-full"
            >
              Try Purgr Free →
            </Link>
          </div>
        </div>

        {/* Related guides */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Related guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/best-free-instagram-unfollow-tracker" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">Best free Instagram unfollow tracker →</p>
            </Link>
            <Link to="/how-to-download-instagram-data" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to download your Instagram data →</p>
            </Link>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default WhyAppsAreDangerous;
